import { useNavigate } from 'react-router-dom'
import { HeartIcon } from '@/components/home/icons'
import { getCasinoGameBadge } from '@/lib/casino'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsCasinoFavorite, setPlayGame, toggleCasinoFavorite } from '@/store/slices/casinoSlice'
import type { CasinoGame } from '@/types/casino'
import { CasinoGameCardImage } from './CasinoGameCardImage'

interface CasinoGameCardProps {
	game: CasinoGame
	className?: string
}

export function CasinoGameCard({ game, className }: CasinoGameCardProps) {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const isFavorite = useAppSelector(selectIsCasinoFavorite(game.id))
	const badge = getCasinoGameBadge(game)

	const handleFavoriteClick = () => {
		dispatch(toggleCasinoFavorite(game.id))
	}

	const handlePlayClick = () => {
		dispatch(setPlayGame(game))
		navigate(`/casino/play/${game.id}`, { state: { game } })
	}

	return (
		<div
			className={cn(
				'group relative h-[200px] w-[150px] shrink-0 transition-[border-color,box-shadow,width] duration-300 ease-in-out',
				'duration-500 animate-in fade-in zoom-in-90 slide-in-from-bottom-[150%] fill-mode-both [animation-timing-function:cubic-bezier(0.22,1.18,0.36,1)]',
				className,
			)}
		>
			<div className='rounded-xxl absolute inset-0 overflow-hidden [clip-path:polygon(0%_0%,100%_0%,100%_100%,75%_100%,65%_95%,35%_95%,25%_100%,0%_100%)]'>
				<CasinoGameCardImage game={game} />
				<div className='pointer-events-none absolute inset-0 rounded-2xl from-secondary/80 via-transparent to-transparent' />

				{badge ? (
					<span className={cn('absolute left-3 top-3 rounded-[6px] border border-black/20 px-1.5 py-0.5 text-sm font-extrabold tracking-[-0.21px]', badge.className)}>
						{badge.label}
					</span>
				) : null}

				<button
					type='button'
					onClick={handleFavoriteClick}
					className={cn(
						'absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-md border border-border bg-secondary text-foreground transition-colors hover:bg-accent',
						isFavorite && 'border-primary/40 bg-primary/10 text-primary',
					)}
					aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
					aria-pressed={isFavorite}
				>
					<HeartIcon className='h-4 w-4' />
				</button>

				<div className='absolute bottom-1 left-1 right-1 flex w-auto translate-y-full flex-col items-center gap-1 overflow-hidden rounded-sm bg-background px-2 py-2.5 text-center opacity-0 transition-all duration-200 [clip-path:polygon(0%_0%,100%_0%,100%_100%,77.25%_100%,66.7%_calc(100%_-_10px),33.3%_calc(100%_-_10px),22.75%_100%,0%_100%)] group-hover:translate-y-0 group-hover:opacity-100'>
					<span className='line-clamp-2 text-sm font-bold uppercase leading-tight tracking-[-0.24px] text-foreground'>{game.casino_name}</span>
					<span className='text-sm font-normal text-subtle-foreground'>{game.provider?.provider_name}</span>
					<button
						type='button'
						onClick={handlePlayClick}
						className='group/play relative mb-2 flex h-8 w-full shrink-0 overflow-hidden rounded-sm border border-primary-border transition-[border-color,box-shadow] duration-200 ease-in-out hover:border-primary'
					>
						<span className='relative flex h-full w-full items-center justify-center overflow-hidden rounded-sm bg-primary px-4 text-sm font-semibold tracking-[-0.21px] text-background sm:px-6 sm:text-[12px] sm:tracking-[-0.24px]'>
							<span
								aria-hidden
								className='pointer-events-none absolute inset-0 rounded-[10px] bg-[radial-gradient(ellipse_90%_85%_at_50%_45%,hsl(var(--primary-highlight))_0%,hsl(var(--primary))_42%,hsl(var(--primary))_100%)] opacity-0 shadow-[inset_0_0_20px_hsl(var(--primary-border)/0.35),inset_0_-8px_16px_hsl(var(--secondary)/0.45)] transition-opacity duration-200 ease-in-out group-hover/play:opacity-70'
							/>
							<span className='relative z-10 inline-block group-hover/play:animate-pulsing'>Play</span>
							<span
								className='absolute bottom-0.5 left-1/2 z-10 h-0.5 w-8 -translate-x-1/2 rounded-full bg-background/80 transition-[width,background-color] duration-200 ease-in-out group-hover/play:w-10 group-hover/play:bg-background'
								aria-hidden
							/>
						</span>
					</button>
				</div>
			</div>

			<div
				aria-hidden
				className='pointer-events-none invisible absolute -inset-[5px] rounded-2xl bg-[linear-gradient(180deg,hsl(var(--primary))_0%,hsl(var(--primary)/0)_80%)] p-[1.5px] opacity-0 shadow-[0_0_2px_0_hsl(var(--primary)/0.20)] transition-[opacity,visibility] duration-300 group-hover:visible group-hover:opacity-100'
				style={{
					WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
					WebkitMaskComposite: 'xor',
					maskComposite: 'exclude',
				}}
			/>

			<span
				className='pointer-events-none absolute bottom-0 left-1/2 z-10 h-1 w-8 -translate-x-1/2 rounded-full bg-border transition-[width,background-color,box-shadow] duration-200 ease-in-out group-hover:w-10 group-hover:bg-primary group-hover:shadow-[0_0_6px_2px_hsl(var(--primary)/0.55)]'
				aria-hidden
			/>
		</div>
	)
}
